import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';

import { ReactComponent as AdminActive } from 'assets/icons/admin-active.svg';
import { ReactComponent as AdminIcon } from 'assets/icons/admin.svg';
import { ReactComponent as AnalyticsActive } from 'assets/icons/analytic-active.svg';
import { ReactComponent as AnalyticsIcon } from 'assets/icons/analytics.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as ClassesActive } from 'assets/icons/classes-active.svg';
import { ReactComponent as ClassesIcon } from 'assets/icons/classes.svg';
import { ReactComponent as ContentActive } from 'assets/icons/content-active.svg';
import { ReactComponent as Content } from 'assets/icons/content.svg';
import { ReactComponent as InstitutionsActive } from 'assets/icons/institutions-active.svg';
import { ReactComponent as Institutions } from 'assets/icons/institutions.svg';
import { ReactComponent as LearnersActive } from 'assets/icons/learners-active.svg';
import { ReactComponent as LearnerIcon } from 'assets/icons/learners.svg';
import { ReactComponent as PermissionsActive } from 'assets/icons/permission-active.svg';
import { ReactComponent as PermissionsIcon } from 'assets/icons/permission.svg';
import { ReactComponent as TrainerActive } from 'assets/icons/trainers-active.svg';
import { ReactComponent as TrainerIcon } from 'assets/icons/trainers.svg';

import type { MenuItem } from 'enable-ui/build/components/Navigation/NavigationBar/core/types';

import { checkRole } from 'helpers';
import { useBoolean } from 'hooks';

import { PATH } from 'constants/path';

export const useController = (isShowMenu: boolean) => {
  const navigationTo = useNavigate();
  const location = useLocation();

  const userProfile = userProfileSelector(useAuthStore.getState());

  const permissions = userProfile?.permission?.webs;

  const [delay, setDelay] = useState(isShowMenu);
  const isFirstMount = useRef(true);

  const [menu, setMenu] = useState<MenuItem[]>([]);

  const isFirstMenu = useBoolean(false);

  const onClickMenuItem = (newItem: MenuItem) => {
    navigationTo(`${newItem.path}`);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isFirstMount.current) {
      isFirstMount.current = false;

      return;
    }

    const elements = document.getElementsByClassName('group-title');
    const titles = document.getElementsByClassName('row-label');

    if (isShowMenu) {
      timer = setTimeout(() => {
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).style.visibility = 'visible';
        }

        for (let i = 0; i < titles.length; i++) {
          (titles[i] as HTMLElement).style.visibility = 'visible';
        }

        setDelay(true);
      }, 350);
    } else {
      setDelay(false);

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).style.visibility = 'hidden';
      }

      for (let i = 0; i < titles.length; i++) {
        (titles[i] as HTMLElement).style.visibility = 'hidden';
      }
    }

    return () => clearTimeout(timer);
  }, [isShowMenu]);

  useEffect(() => {
    if (!permissions || isFirstMenu.value) return;

    const newMenu: MenuItem[] = [];

    if (permissions.includes('Institutions')) {
      newMenu.push({
        label: 'Institutions',
        path: `${PATH.INSTITUTIONS}`,
        icon: location.pathname.includes(PATH.INSTITUTIONS) ? (
          <InstitutionsActive />
        ) : (
          <Institutions />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Classes')) {
      newMenu.push({
        label: 'Classes',
        path: `${PATH.CLASSES}`,
        icon: location.pathname.includes(PATH.CLASSES) ? <ClassesActive /> : <ClassesIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Admins')) {
      newMenu.push({
        label: 'Admins',
        path: `${PATH.ADMINS}`,
        icon: location.pathname.includes(PATH.ADMINS) ? <AdminActive /> : <AdminIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Clients')) {
      newMenu.push({
        label: 'Clients',
        path: `${PATH.LEARNERS}`,
        icon: location.pathname.includes(PATH.LEARNERS) ? <LearnersActive /> : <LearnerIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Trainers')) {
      newMenu.push({
        label: 'Trainers',
        path: `${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME}`,
        icon: location.pathname.includes(`${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME}`) ? (
          <TrainerActive />
        ) : (
          <TrainerIcon />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Library')) {
      newMenu.push({
        label: 'Library',
        path: `${PATH.CONTENT}`,
        icon: location.pathname.includes(PATH.CONTENT) ? <ContentActive /> : <Content />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Analytics')) {
      newMenu.push({
        label: 'Analytics',
        path: `${PATH.ANALYTICS}`,
        icon: location.pathname.includes(PATH.ANALYTICS) ? <AnalyticsActive /> : <AnalyticsIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Institution info')) {
      newMenu.push({
        label: 'Institution info',
        path: `${PATH.INSTITUTION_INFO}`,
        icon: location.pathname.includes(PATH.INSTITUTION_INFO) ? (
          <InstitutionsActive />
        ) : (
          <Institutions />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    if (permissions.includes('Calendar')) {
      newMenu.push({
        label: 'Calendar',
        path: `${PATH.CALENDAR}`,
        icon: <CalendarIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      });
    }

    setMenu(newMenu);

    isFirstMenu.setValue(true);
  }, [JSON.stringify(permissions)]);

  const biologicAdminMenus: { [key: string]: MenuItem[] } = {
    'BIOLOGIC PROPERTIES': [
      {
        label: 'Institutions',
        path: `${PATH.INSTITUTIONS}`,
        icon: location.pathname.includes(PATH.INSTITUTIONS) ? (
          <InstitutionsActive />
        ) : (
          <Institutions />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Admins',
        path: `${PATH.ADMINS}`,
        icon: location.pathname.includes(PATH.ADMINS) ? <AdminActive /> : <AdminIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Library',
        path: `${PATH.CONTENT}`,
        icon: location.pathname.includes(PATH.CONTENT) ? <ContentActive /> : <Content />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Role permissions',
        path: `${PATH.PERMISSIONS}`,
        icon: location.pathname.includes(PATH.PERMISSIONS) ? (
          <PermissionsActive />
        ) : (
          <PermissionsIcon />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
    ],
    'INSTITUTION PROPERTIES': [
      {
        label: 'Classes',
        path: `${PATH.CLASSES}`,
        icon: location.pathname.includes(PATH.CLASSES) ? <ClassesActive /> : <ClassesIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Trainers',
        path: `${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME}`,
        icon: location.pathname.includes(`${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME}`) ? (
          <TrainerActive />
        ) : (
          <TrainerIcon />
        ),
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Clients',
        path: `${PATH.LEARNERS}`,
        icon: location.pathname.includes(PATH.LEARNERS) ? <LearnersActive /> : <LearnerIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
      {
        label: 'Calendar',
        path: PATH.CALENDAR,
        icon: <CalendarIcon />,
        width: '196px',
        onClickMenuItem: onClickMenuItem,
      },
    ],
  };

  const menus: { [key: string]: MenuItem[] } = {
    '': menu,
  };

  return {
    menus: checkRole([RoleKey.BIOLOGIC_ADMIN]) ? biologicAdminMenus : menus,
    locationPathName: location.pathname,
    delay,
  };
};
